import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
} from '@angular/core';

import { OfferSource } from '@cds';
import { EventModel, EventOption, EventOptionGroup, EventParticipantImage, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { MarketType } from '@frontend/sports/betting-offer/feature/types';
import { GridConfig as MsGrid, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { CouponTypes } from '@frontend/sports/coupons/feature/models';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { GridEvent, GridMedia } from '@frontend/sports/grid/core/feature/model';
import { ParticipantImageService } from '@frontend/sports/participant-image/feature';
import { NOT_APPLICABLE, TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButton } from '@frontend/ui/button';
import { DsPill } from '@frontend/ui/pill';
import { DeviceService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { cloneDeep, groupBy, keys, sortBy, uniqBy } from 'lodash-es';

import { SourceOfPick } from '../../../betslip-base/models/pick-models';
import { EpcotModule } from '../../../common/epcot-config.service';
import { ScoreboardSlimSubscriptionService } from '../../../event-subscription/scoreboard-slim-subscription.service';
import { GridOptionGroupComponent } from '../../../grid/grid-option-group.component';
import { EventHeaderComponent } from '../../../grid/header/event-header.component';
import { UrlHelperService } from '../../../navigation-core/url-helper.service';
import { OptionGroupRendererDirective } from '../../../option-group/option-group-renderer.directive';
import { TabBarItem } from '../../../tab-bar/tab-bar.models';

export interface GoalScoreHeader {
    name: string;
    optionColumnID: number;
    active: boolean;
}

export interface EventDetail {
    expanded: boolean;
    trackDetails: string;
}
@Component({
    selector: 'ms-goal-scorer-option-group',
    templateUrl: 'goal-scorer-option-group.component.html',
    styleUrls: ['./goal-scorer-option-group.component.scss'],
    providers: [ParticipantImageService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, GridOptionGroupComponent, EventHeaderComponent, DsPill, DsButton, OptionGroupRendererDirective, IconCustomComponent],
})
export class GoalScorerOptionGroupComponent implements OnInit, OnChanges, OnDestroy {
    @Input() event: GridEvent;
    @Input() tracking: TrackingSource;
    @Input() betDetails = '';
    @Input() showParticipantImage: boolean;
    @Input() media: GridMedia;
    @Input() periodGroup: number;

    @Output() formSwitch = new EventEmitter();

    activeOptionGroup: EventOptionGroup;
    moreOptionGroup: EventOptionGroup;
    goalScoreOptionGroups: EventOptionGroup[] = [];
    formPills: TabBarItem[] = [];
    isShowMore = false;
    isEventNameALink: boolean;
    headers: GoalScoreHeader[];
    showHeaders: boolean;
    showAllOptions: boolean;
    module = EpcotModule.Grid;
    private showMoreRowsThreshold: number;
    optionImages: NumberDictionary<EventParticipantImage> = {};
    enableOptionImages: boolean;
    sourceOfPick = SourceOfPick.Other;
    isExpanded = true;
    eventUrl: string;
    hasPillExist = false;
    isMobile = false;
    isAllBetsButtonVisible: boolean;
    showMoreLessText: string;
    couponType: string;
    scoreOptionsClass: string;
    showMoreItemsClass: string;
    lastRowOptionsCount: number;
    lastRowOptionsCountMore: number;
    columnsCount: number;
    private pushSubscription?: EventSubscription<EventModel>;
    scoreBoardEvent: GridEvent;

    constructor(
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
        private sitecore: Sitecore,
        private msGrid: MsGrid,
        private changeDetectorRef: ChangeDetectorRef,
        private urlHelper: UrlHelperService,
        private trackingService: TrackingService,
        private participantImageService: ParticipantImageService,
        private deviceService: DeviceService,
        private sbSlimSubscriptionService: ScoreboardSlimSubscriptionService,
    ) {
        this.isEventNameALink = this.gridConfig?.isEventNameALink ?? false;
        this.isMobile = this.deviceService.isMobilePhone;
        this.isAllBetsButtonVisible = this.gridConfig?.isAllBetsButtonVisible ?? true;
        this.showMoreLessText = this.sitecore.event.ShowMore;
        this.couponType = CouponTypes.GoalScorer;
        this.showMoreRowsThreshold = this.msGrid.showMoreRowsThreshold;
        this.showMoreItemsClass = '';
        this.columnsCount = this.isMobile ? 2 : 3;
    }

    @HostBinding('class') className = 'goal-scorer-container';

    ngOnInit(): void {
        if (!this.event) {
            return;
        }
        this.setLink();
        this.setLastRowOptionsCount();
        this.subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.event?.optionGroups?.length) {
            return;
        }
        if (this.scoreBoardEvent) {
            this.event.scoreboard = this.scoreBoardEvent.scoreboard;
            this.event.live = this.scoreBoardEvent.scoreboard.started;
        }
        if (changes?.periodGroup?.previousValue !== changes?.periodGroup?.currentValue) {
            this.reset();
        }
        this.setLink();
        this.goalScoreOptionGroups = this.event.optionGroups && this.periodGroup ? this.getGoalScoreOptionGroups() : [];
        if (this.goalScoreOptionGroups?.length) {
            this.setActiveGroup();
        }
    }

    private setLastRowOptionsCount() {
        this.lastRowOptionsCount = 0;
        if (this.columnsCount && this.activeOptionGroup?.options?.length > 0) {
            this.lastRowOptionsCount = this.activeOptionGroup?.options?.length % this.columnsCount;
            if (!this.hasMore) {
                this.lastRowOptionsCount = this.lastRowOptionsCount === 0 ? this.columnsCount : this.lastRowOptionsCount;
            }
        }
    }

    private setlastRowOptionsCountForMoreSection() {
        this.lastRowOptionsCountMore = 0;
        if (this.columnsCount && this.moreOptionGroup?.options?.length > 0) {
            this.lastRowOptionsCountMore = this.moreOptionGroup?.options?.length % this.columnsCount;
            this.lastRowOptionsCountMore = this.lastRowOptionsCountMore === 0 ? this.columnsCount : this.lastRowOptionsCountMore;
        }
    }

    private reset(): void {
        this.formPills = [];
        this.goalScoreOptionGroups = [];
        this.moreOptionGroup = Object.assign({});
        this.activeOptionGroup = Object.assign({});
        this.showAllOptions = false;
        this.isExpanded = true;
        this.isShowMore = false;
    }

    private setActiveGroup(): void {
        const activeIndex = this.getActiveIndex();
        if (this.formPills.length > 0) {
            this.formPills[activeIndex].active = true;
        }
        const activeOptionGroup = this.getActiveOptionGroup();
        if (Object.keys(activeOptionGroup).length > 0) {
            this.init(activeOptionGroup);
            this.hasPillExist = true;
        }
    }

    private init(activeOptionGroup: EventOptionGroup): void {
        if (this.event.offerSource === OfferSource.V2) {
            activeOptionGroup.options?.forEach((data) => {
                if (data.playerId) {
                    data.teamId = this.event.players.find((player) => player.fixtureParticipantId === data.playerId)?.teamId;
                    if (data.teamId) {
                        data.image = this.event.participants.find((participant) => participant.id === data.teamId)?.image;
                    } else {
                        data.playerId = undefined;
                    }
                }
            });
        }
        [this.enableOptionImages, this.optionImages] = this.participantImageService.showOrHideOptionImages(
            activeOptionGroup.options,
            this.event.sport?.id,
            this.event.offerSource,
        );

        this.activeOptionGroup = cloneDeep(activeOptionGroup);
        const scoreRowsCount = this.activeOptionGroup.options.length;
        this.showAllOptions = scoreRowsCount <= this.showMoreRowsThreshold;

        if (!this.showAllOptions) {
            this.moreOptionGroup = Object.assign({}, this.activeOptionGroup);
            this.moreOptionGroup.options = [];
            this.moreOptionGroup.options = this.activeOptionGroup.options.splice(
                this.showMoreRowsThreshold,
                scoreRowsCount - this.showMoreRowsThreshold,
            );
            this.showMoreItemsClass = 'showmore-items';
        } else {
            this.moreOptionGroup = Object.assign({});
        }
    }

    private getGoalScoreOptionGroups(): EventOptionGroup[] {
        const goalScoreOptionGroups = this.event.optionGroups?.filter(
            (og) =>
                (og.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.Scorer ||
                    og.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.ScorerWithoutNoScoreOption) &&
                og.options[0]?.optionColumnId !== undefined,
        );

        return goalScoreOptionGroups;
    }

    private getActiveIndex(): number {
        let activeIndex = this.getActiveHeaders();
        if (activeIndex < 0) {
            activeIndex = 0;
        }

        return activeIndex;
    }

    private getActiveOptionGroup(): EventOptionGroup {
        const activeId = this.formPills?.find((pill) => pill.active)?.id;
        const optionGroup =
            this.goalScoreOptionGroups?.find(
                (og) => og.detailedGrouping?.periodGroup === this.periodGroup && og.options[0]?.optionColumnId === activeId,
            ) ?? new EventOptionGroup();

        return optionGroup;
    }

    private getActiveHeaders(): number {
        if (this.formPills.length === 0) {
            this.goalScoreOptionGroups.forEach((og) => {
                if (og.detailedGrouping?.periodGroup === this.periodGroup) {
                    const optionHeaders = this.getOptionHeaders(og.options);
                    this.headers = uniqBy(optionHeaders, (header) => header.optionColumnID);
                    this.showHeaders = !!this.headers.filter((x) => x.name)?.length;
                    if (this.showHeaders) {
                        this.headers.forEach((data) => {
                            this.formPills.push({
                                title: data.name,
                                id: data.optionColumnID,
                                active: false,
                            });
                        });
                        this.formPills = sortBy(
                            uniqBy(this.formPills, (formPill) => formPill.id),
                            (formPill) => formPill.id,
                        );
                    }
                }
            });
        }

        return this.formPills.findIndex((pill) => pill.active);
    }

    onFormSwitch(pill: TabBarItem): void {
        this.formPills = this.formPills.map((item) => ({
            ...item,
            active: item.id === pill.id,
        }));
        this.formSwitch.emit(pill.id);
        this.isShowMore = false;
        this.changeDetectorRef.detectChanges();
    }

    private getOptionHeaders(optionGroup: EventOption[]): GoalScoreHeader[] {
        const groups = groupBy(optionGroup, (option) => option.optionColumnId);

        return keys(groups).map(
            (key) => <GoalScoreHeader>{ name: groups[key][0].optionColumnName || '', optionColumnID: groups[key][0].optionColumnId, active: false },
        );
    }

    private setLink(): void {
        this.eventUrl = this.urlHelper.getEventUrl(this.event);
    }

    onShowMoreLessButtonToggle(): void {
        this.isShowMore = !this.isShowMore;
        this.changeDetectorRef.detectChanges();
        this.showMoreLessText = this.isShowMore ? this.sitecore.event.ShowLess : this.sitecore.event.ShowMore;
        const trackEvent = this.isShowMore ? this.sitecore.event.ShowMore : this.sitecore.event.ShowLess;
        this.trackData(trackEvent);
        if (this.isShowMore) {
            this.setlastRowOptionsCountForMoreSection();
        }
    }

    getEventDetails(eventDetail: EventDetail): void {
        if (this.hasContainer) {
            this.isExpanded = eventDetail.expanded;
            this.isShowMore = false;
            this.showMoreLessText = this.sitecore.event.ShowMore;
            this.trackData(eventDetail.trackDetails.toLowerCase());
        }
    }

    get hasContainer(): boolean {
        return this.hasPillExist && this.activeOptionGroup?.options?.length > 0;
    }

    get hasMore(): boolean {
        return !this.showAllOptions && this.moreOptionGroup?.options?.length > 0 && this.isExpanded;
    }

    private trackData(eventDetails: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports coupons',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'goal scorer',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    togglePrevent(event: Event): void {
        event.stopPropagation();
    }

    trackItem(option: EventOption): number {
        return option.id;
    }

    private subscribe(): void {
        if (this.event) {
            this.pushSubscription = this.sbSlimSubscriptionService.subscribe([{ event: this.event, callback: this.updateScoreboard }]);
        }
    }

    protected updateScoreboard = (updatedEvent: GridEvent): void => {
        if (updatedEvent) {
            this.event = cloneDeep(updatedEvent);
            this.scoreBoardEvent = cloneDeep(updatedEvent);
        }
    };

    ngOnDestroy(): void {
        this.pushSubscription?.unsubscribe();
    }
}
